import {announce} from '@github-ui/aria-live'
import {useSearchParams} from '@github-ui/use-navigate'
import {ChevronDownIcon, ChevronRightIcon} from '@primer/octicons-react'
import {CounterLabel, IconButton} from '@primer/react'
import {useCallback, useId, useMemo, useRef, useState} from 'react'

import {LABELS} from '../../constants'
import type {SavedView, SavedViewVisibility} from '../../types'
import type {PinControls} from './PinViewMenuItem'
import styles from './RepositoryViews.module.css'
import {applySectionSort, resolveSectionSort} from './views-section-sort'
import {sortViews, type ViewsSortValue} from './views-sort-options'
import {ViewsList} from './ViewsList'
import {ViewsSortMenu} from './ViewsSortMenu'

const SECTION_PREVIEW_COUNT = 10

const SECTION_TITLES: Record<SavedViewVisibility, string> = {
  personal: LABELS.views.personal,
  team: LABELS.views.team,
}

type ViewsSectionProps = {
  visibility: SavedViewVisibility
  views: SavedView[]
  otherSectionsHaveViews: boolean
  owner: string
  repo: string
  viewerCanManageViews?: boolean
  showPinControls?: boolean
  pinControls?: PinControls
}

export function ViewsSection({
  visibility,
  views,
  otherSectionsHaveViews,
  owner,
  repo,
  viewerCanManageViews = false,
  showPinControls = false,
  pinControls,
}: ViewsSectionProps) {
  const title = SECTION_TITLES[visibility]
  const headingId = useId()
  const bodyRef = useRef<HTMLDivElement>(null)
  const headingRef = useRef<HTMLHeadingElement>(null)
  const [searchParams, setSearchParams] = useSearchParams()
  const sort = resolveSectionSort(searchParams, visibility)
  const [expanded, setExpanded] = useState(false)
  const [userCollapsed, setUserCollapsed] = useState<boolean | undefined>(undefined)

  const handleSortChange = useCallback(
    (next: ViewsSortValue) => {
      setSearchParams(prev => applySectionSort(prev, visibility, next), {replace: true, preventAutofocus: true})
    },
    [setSearchParams, visibility],
  )

  const sortedViews = useMemo(
    () => sortViews(views, sort, {pinnedFirst: showPinControls}),
    [views, sort, showPinControls],
  )
  const hasViews = views.length > 0
  const collapsed = userCollapsed ?? !hasViews
  const visibleViews = expanded ? sortedViews : sortedViews.slice(0, SECTION_PREVIEW_COUNT)
  const hasMore = sortedViews.length > visibleViews.length
  const section = useMemo(
    () => ({
      title,
      isLastView: (deletedId: SavedView['id']) => sortedViews.length === 1 && sortedViews[0]?.id === deletedId,
      otherSectionsHaveViews,
      headingRef,
    }),
    [title, sortedViews, otherSectionsHaveViews],
  )

  const revealAll = () => {
    setExpanded(true)
    announce(`Showing all ${sortedViews.length} ${title} views`)
    requestAnimationFrame(() => {
      const rows = bodyRef.current?.querySelectorAll<HTMLElement>('[data-saved-view-id]')
      rows?.[SECTION_PREVIEW_COUNT]?.focus()
    })
  }

  return (
    <section className={styles.section} aria-labelledby={headingId}>
      <div className={styles.sectionHeader}>
        <div className={styles.sectionTitleGroup}>
          <IconButton
            className={styles.collapseButton}
            icon={collapsed ? ChevronRightIcon : ChevronDownIcon}
            aria-label={collapsed ? `Expand ${title}` : `Collapse ${title}`}
            aria-expanded={!collapsed}
            variant="invisible"
            size="small"
            onClick={() => setUserCollapsed(!collapsed)}
          />
          <h2 id={headingId} ref={headingRef} className={styles.sectionHeading}>
            {title}
          </h2>
          <CounterLabel>{views.length}</CounterLabel>
        </div>
        {hasViews && !collapsed && <ViewsSortMenu sort={sort} onSortChange={handleSortChange} />}
      </div>
      {!collapsed && (
        <div ref={bodyRef} className={styles.viewsListWrapper}>
          <ViewsList
            views={visibleViews}
            owner={owner}
            repo={repo}
            sort={sort}
            onSortChange={handleSortChange}
            viewerCanManageViews={viewerCanManageViews}
            showPinControls={showPinControls}
            pinControls={pinControls}
            title={title}
            ariaLabelledBy={headingId}
            section={section}
            hideMetadata
          />
          {hasMore && (
            <button
              type="button"
              className={styles.showMore}
              aria-label={`Show more ${title} views`}
              onClick={revealAll}
            >
              {LABELS.views.showMore}
            </button>
          )}
        </div>
      )}
    </section>
  )
}
